/**
 * Status Management Utilities
 * 
 * Functions for managing statuses: grouping, sorting, validation, CRUD operations.
 */

import { DEFAULT_STATUSES, STATUS_CATEGORIES, CATEGORY_ORDER } from '../TaskNode/constants';
import { getStatusCategory } from './statusConfig';

/**
 * Get tasks by category
 * @param {Array} tasks - Array of tasks
 * @param {string} category - Category ID
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {Array} Filtered tasks
 */
export const getTasksByCategory = (tasks, category, statuses = DEFAULT_STATUSES) => {
  return tasks.filter(task => getStatusCategory(task.status, statuses) === category);
};

/**
 * Group tasks by their status, sorted by kanbanOrder within each group
 * @param {Array} tasks - Array of tasks
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {Object} Tasks grouped by status ID, sorted by kanbanOrder
 */
export const groupTasksByStatus = (tasks, statuses = DEFAULT_STATUSES) => {
  const grouped = {};
  
  // Initialize all status buckets
  statuses.forEach(status => {
    grouped[status.id] = [];
  });
  
  // Group tasks
  tasks.forEach(task => {
    const statusId = task.status || 'pending';
    if (grouped[statusId]) {
      grouped[statusId].push(task);
    } else {
      // Fallback to first "not_started" status
      const fallbackStatus = statuses.find(s => s.category === 'not_started');
      if (fallbackStatus && grouped[fallbackStatus.id]) {
        grouped[fallbackStatus.id].push(task);
      }
    }
  });
  
  // Sort each group by kanbanOrder (tasks without kanbanOrder go to the end)
  Object.keys(grouped).forEach(statusId => {
    grouped[statusId].sort((a, b) => {
      const orderA = a.kanbanOrder ?? Infinity;
      const orderB = b.kanbanOrder ?? Infinity;
      return orderA - orderB;
    });
  });
  
  return grouped;
};

/**
 * Generate a unique status ID from a name
 * @param {string} name - Status name
 * @param {Array} existingStatuses - Existing statuses to avoid conflicts
 * @returns {string} Unique status ID
 */
export const generateStatusId = (name, existingStatuses = []) => {
  // Convert to lowercase, replace spaces with underscores, remove special chars
  let baseId = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  
  // Ensure uniqueness
  let id = baseId;
  let counter = 1;
  while (existingStatuses.some(s => s.id === id)) {
    id = `${baseId}_${counter}`;
    counter++;
  }
  
  return id;
};

/**
 * Sort statuses by category order, then by order within category
 * Category order: not_started → in_progress → blocked → completed
 * @param {Array} statuses - Array of status objects
 * @returns {Array} Sorted statuses
 */
export const sortStatusesByCategory = (statuses = DEFAULT_STATUSES) => {
  return [...statuses].sort((a, b) => {
    // First sort by category order
    const categoryOrderA = CATEGORY_ORDER.indexOf(a.category);
    const categoryOrderB = CATEGORY_ORDER.indexOf(b.category);
    
    if (categoryOrderA !== categoryOrderB) {
      return categoryOrderA - categoryOrderB;
    }
    
    // Within same category, sort by order field
    return (a.order || 0) - (b.order || 0);
  });
};

/**
 * Required categories - must always have at least one status of each
 */
const REQUIRED_CATEGORIES = ['not_started', 'in_progress', 'blocked', 'completed'];

/**
 * Priority names for smart status matching
 * When auto-selecting a status (e.g., for decisions or nested campaigns),
 * we prefer statuses with these names (in order) before falling back to
 * the first available status in the category.
 */
const PRIORITY_STATUS_NAMES = {
  completed: ['done', 'finished', 'completed', 'complete'],
  in_progress: ['in progress', 'inprogress', 'working', 'active', 'started'],
  not_started: ['to do', 'todo', 'pending', 'not started', 'notstarted', 'backlog'],
  blocked: ['blocked', 'on hold', 'onhold', 'waiting', 'stuck'],
};

/**
 * Normalize a string for comparison
 * Converts to lowercase and removes all separators (spaces, underscores, hyphens)
 * @param {string} str - String to normalize
 * @returns {string} Normalized string
 */
const normalizeStatusName = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/[\s_-]/g, '');
};

/**
 * Find the preferred status for a given category
 * Uses smart name matching to find common status names before falling back
 * to the first available status in the category.
 * 
 * @param {Array} statuses - The campaign's custom statuses array
 * @param {string} category - The category to find a status for ('completed', 'in_progress', etc.)
 * @returns {Object|null} The preferred status object, or null if none found
 * 
 * @example
 * // If statuses include "Done", "Finished", "Archived" (all completed category)
 * findPreferredStatus(statuses, 'completed') // Returns "Done" status
 * 
 * @example
 * // If statuses include "Working On It", "In Review" (all in_progress category)
 * findPreferredStatus(statuses, 'in_progress') // Returns "Working On It" (matches "working")
 */
export const findPreferredStatus = (statuses = DEFAULT_STATUSES, category) => {
  // Get all statuses in this category, sorted by order
  const categoryStatuses = sortStatusesByCategory(statuses)
    .filter(s => s.category === category);
  
  if (categoryStatuses.length === 0) return null;
  
  // Get priority names for this category
  const priorityNames = PRIORITY_STATUS_NAMES[category] || [];
  
  // Try to find a status matching priority names (in order)
  for (const priorityName of priorityNames) {
    const normalizedPriority = normalizeStatusName(priorityName);
    const match = categoryStatuses.find(s => 
      normalizeStatusName(s.name) === normalizedPriority
    );
    if (match) return match;
  }
  
  // No priority match found - return first status in category
  return categoryStatuses[0];
};

/**
 * Check if a status can be deleted
 * @param {string} statusId - The status ID to check
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {Object} { canDelete: boolean, reason?: string }
 */
export const canDeleteStatus = (statusId, statuses = DEFAULT_STATUSES) => {
  const status = statuses.find(s => s.id === statusId);
  if (!status) {
    return { canDelete: false, reason: 'Status not found' };
  }
  
  // Check if this is the last status of a required category
  if (REQUIRED_CATEGORIES.includes(status.category)) {
    const statusesInCategory = statuses.filter(s => s.category === status.category);
    if (statusesInCategory.length <= 1) {
      const categoryLabel = STATUS_CATEGORIES[status.category]?.label || status.category;
      return { 
        canDelete: false, 
        reason: `Cannot delete: Must have at least one "${categoryLabel}" status` 
      };
    }
  }
  
  return { canDelete: true };
};

/**
 * Get the fallback status when a status is deleted
 * Returns the first status of the same category, or the first not_started status
 * @param {string} deletedStatusId - The status being deleted
 * @param {Array} statuses - The campaign's custom statuses array
 * @returns {string} The fallback status ID
 */
export const getFallbackStatus = (deletedStatusId, statuses = DEFAULT_STATUSES) => {
  const deletedStatus = statuses.find(s => s.id === deletedStatusId);
  if (!deletedStatus) return statuses[0]?.id || 'pending';
  
  // Find another status in the same category
  const sameCategory = statuses.find(s => 
    s.category === deletedStatus.category && s.id !== deletedStatusId
  );
  if (sameCategory) return sameCategory.id;
  
  // Fallback to first not_started status
  const notStarted = statuses.find(s => s.category === 'not_started');
  if (notStarted) return notStarted.id;
  
  // Last resort - first status
  return statuses[0]?.id || 'pending';
};
